import { Permissions } from '@onesource/schemas';
import { useGetAllProfileQuery } from '@onesource/services';
import { useAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';

/**
 * Session-scoped toggle that lets reviewers/admins preview unpublished (draft)
 * Community Hub content. Backed by sessionStorage so it survives navigation and
 * refresh but resets when the tab closes.
 */
const previewToggleAtom = atomWithStorage<boolean>(
  'ch-preview-mode',
  false,
  createJSONStorage(() => sessionStorage),
);

/**
 * Whether the current user may preview drafts in the space currently being
 * viewed. Provided per-space by PreviewModeProvider so the gate matches the
 * backend (which checks content.edit / space.manage for *this space's* app),
 * instead of a global "has the permission in any app" check.
 */
const PreviewCapabilityContext = createContext<boolean>(false);

interface PreviewModeProviderProps {
  /** The current (parent) space's app key, used to look up the user's role. */
  app?: string | null;
  children: ReactNode;
}

export const PreviewModeProvider = ({ app, children }: PreviewModeProviderProps) => {
  const { data: profile } = useGetAllProfileQuery();

  const canPreview = useMemo(() => {
    if (!app) {
      return false;
    }
    const permissions = profile?.access?.[app]?.permissions ?? [];
    return permissions.some(p => p.name === Permissions.CONTENT_EDIT || p.name === Permissions.SPACE_MANAGE);
  }, [app, profile]);

  return <PreviewCapabilityContext.Provider value={canPreview}>{children}</PreviewCapabilityContext.Provider>;
};

interface UsePreviewModeResult {
  /** True only when the toggle is on AND the user may preview this space. */
  isPreview: boolean;
  /** Whether the current user may use preview mode in this space. */
  canPreview: boolean;
  /** Flip the toggle. No-op for users without permission in this space. */
  togglePreview: () => void;
}

/**
 * Preview mode reuses the content-edit / space-manage capability, scoped to the
 * space being viewed (via PreviewModeProvider). Gating is re-evaluated from live
 * permissions so a stale stored flag can never reveal drafts to a member.
 */
export const usePreviewMode = (): UsePreviewModeResult => {
  const canPreview = useContext(PreviewCapabilityContext);
  const [stored, setStored] = useAtom(previewToggleAtom);

  const togglePreview = useCallback(() => {
    if (!canPreview) {
      return;
    }
    setStored(prev => !prev);
  }, [canPreview, setStored]);

  return {
    isPreview: canPreview && stored,
    canPreview,
    togglePreview,
  };
};
